import type { Point2 } from './person-motion';

/**
 * Grille de circulation d'un étage, tirée de la géométrie du GLB du site (ADR-017, démo) : le
 * personnage 3D d'un tracker contourne les murs et passe par les portes. Fonctions pures, sans
 * three.js (les triangles arrivent déjà en coordonnées du modèle, y vers le haut).
 *
 * - plancher : triangles horizontaux à ± 0,3 m du plancher de l'étage → cellules marchables ;
 * - murs : triangles coupant la tranche 1,0–1,8 m au-dessus du plancher (le mobilier bas n'y est pas) ;
 * - portes : le mur est percé sur toute son épaisseur, sous chaque battant (`Doors`) et entre les
 *   deux montants de chaque cadre (`Walls_Door_Frames`), double porte comprise quand chaque montant
 *   porte un battant : dans ce GLB, les murs sont souvent
 *   continus derrière les portes ; l'ouverture reste libre même avec la marge aux murs ;
 * - dehors (RDC seulement, `outdoorM`) : le sol autour du bâtiment est praticable jusqu'à cette
 *   distance ; on n'y passe que par les ouvertures de la façade, jamais à travers.
 */
export interface NavGridInput {
  /** Hauteur du plancher de l'étage (y du modèle). */
  floorY: number;
  /** Côté d'une cellule (m). */
  cellM?: number;
  /** Marge gardée le long des murs (m). */
  clearanceM?: number;
  /** Triangles à plat : [x, y, z] × 3 par triangle. */
  floor: ArrayLike<number>;
  walls: ArrayLike<number>;
  doors: ArrayLike<number>;
  /** Cadres de portes (`Walls_Door_Frames`) : leurs montants, appariés, marquent une ouverture. */
  frames?: ArrayLike<number>;
  /** Sol praticable autour du bâtiment jusqu'à cette distance (m) ; 0 ou absent : pas de dehors. */
  outdoorM?: number;
}

const WALL_SLICE = [1.0, 1.8] as const;
/** Coût d'une cellule de mur franchie (pièce sans ouverture modélisée) : une porte est toujours préférée. */
const WALL_CROSSING_COST = 60;
const DOOR_SLICE = [0.2, 2.0] as const;
const FLOOR_TOLERANCE_M = 0.3;
/** Demi-épaisseur percée de part et d'autre de l'axe d'une porte (m). */
const OPENING_HALF_DEPTH_M = 0.3;
/** Écart entre les deux montants d'un même cadre (m) : en dehors, ce ne sont pas les deux côtés d'une porte. */
const JAMB_PAIR_M = [0.7, 1.4] as const;
/** Double porte : deux montants plus écartés, reliés seulement si chacun porte un battant. */
const DOUBLE_DOOR_M = 2.6;
/** Distance entre l'extrémité d'un battant et le montant qui le porte (m). */
const HINGE_M = 0.25;
/** Cadre d'un seul bloc (montants + traverse ou battant fondus) : longueur d'une porte, et fin. */
const BLOCK_DOOR_M = [0.6, 1.4] as const;
const BLOCK_DOOR_MAX_DEPTH_M = 0.6;

export class NavGrid {
  private constructor(
    private readonly x0: number,
    private readonly z0: number,
    private readonly cell: number,
    private readonly nx: number,
    private readonly nz: number,
    private readonly walkable: Uint8Array,
    /** Cellules sur l'emprise du plancher (marchables ou non) : seules franchissables en dernier recours. */
    private readonly onFloor: Uint8Array
  ) {}

  static build(input: NavGridInput): NavGrid {
    // 10 cm : à 20 cm, la marge aux murs refermait les passages étroits du GLB de LA-TEST-MAJ.
    const cell = input.cellM ?? 0.1;
    const fy = input.floorY;
    const floorTris = horizontalNear(input.floor, fy, FLOOR_TOLERANCE_M);
    const bounds = bbox(floorTris.length ? floorTris : Array.from(input.walls));
    const outdoor = Math.max(0, input.outdoorM ?? 0);
    const x0 = bounds.minX - outdoor - cell;
    const z0 = bounds.minZ - outdoor - cell;
    const nx = Math.max(1, Math.ceil((bounds.maxX + outdoor - x0) / cell) + 2);
    const nz = Math.max(1, Math.ceil((bounds.maxZ + outdoor - z0) / cell) + 2);
    const mask = () => new Uint8Array(nx * nz);
    const floor = mask();
    const wall = mask();
    const door = mask();
    const mark = (m: Uint8Array, x: number, z: number) => {
      const ix = Math.floor((x - x0) / cell);
      const iz = Math.floor((z - z0) / cell);
      if (ix >= 0 && iz >= 0 && ix < nx && iz < nz) {
        m[iz * nx + ix] = 1;
      }
    };
    fillTriangles(floorTris, cell, (x, z) => mark(floor, x, z), x0, z0, nx, nz);
    sampleTriangles(input.walls, cell, fy + WALL_SLICE[0], fy + WALL_SLICE[1], (x, z) => mark(wall, x, z));
    sampleTriangles(input.doors, cell, fy + DOOR_SLICE[0], fy + DOOR_SLICE[1], (x, z) => mark(door, x, z));
    const jambs = mask();
    sampleTriangles(input.frames ?? [], cell, fy + DOOR_SLICE[0], fy + DOOR_SLICE[1], (x, z) => mark(jambs, x, z));

    // Percements : sous chaque battant, et entre les deux montants de chaque cadre.
    const toWorld = (c: number) => ({ x: x0 + ((c % nx) + 0.5) * cell, z: z0 + (Math.floor(c / nx) + 0.5) * cell });
    const opening = mask();
    const carve = (c: { x: number; z: number }, axis: { x: number; z: number }, halfLen: number) => {
      const reach = Math.hypot(halfLen, OPENING_HALF_DEPTH_M);
      const ix0 = Math.max(0, Math.floor((c.x - reach - x0) / cell));
      const ix1 = Math.min(nx - 1, Math.floor((c.x + reach - x0) / cell));
      const iz0 = Math.max(0, Math.floor((c.z - reach - z0) / cell));
      const iz1 = Math.min(nz - 1, Math.floor((c.z + reach - z0) / cell));
      for (let iz = iz0; iz <= iz1; iz++) {
        for (let ix = ix0; ix <= ix1; ix++) {
          const p = toWorld(iz * nx + ix);
          const t = (p.x - c.x) * axis.x + (p.z - c.z) * axis.z;
          const n = -(p.x - c.x) * axis.z + (p.z - c.z) * axis.x;
          if (Math.abs(t) <= halfLen && Math.abs(n) <= OPENING_HALF_DEPTH_M) opening[iz * nx + ix] = 1;
        }
      }
    };
    const leafEnds: { x: number; z: number }[] = [];
    for (const leaf of components(door, nx, nz)) {
      const pts = leaf.map(toWorld);
      const c = centroid(pts);
      const axis = principalAxis(pts, c);
      const halfLen = Math.max(...pts.map(p => Math.abs((p.x - c.x) * axis.x + (p.z - c.z) * axis.z))) + cell / 2;
      carve(c, axis, halfLen);
      leafEnds.push(
        { x: c.x - axis.x * halfLen, z: c.z - axis.z * halfLen },
        { x: c.x + axis.x * halfLen, z: c.z + axis.z * halfLen }
      );
    }
    // Un cadre d'un seul tenant, de la longueur d'une porte, est une ouverture ; un petit est un montant
    // (à apparier) ; un long (cloison vitrée) reste fermé.
    const posts: { x: number; z: number }[] = [];
    for (const cells of components(jambs, nx, nz)) {
      const pts = cells.map(toWorld);
      const c = centroid(pts);
      const axis = principalAxis(pts, c);
      const along = pts.map(p => (p.x - c.x) * axis.x + (p.z - c.z) * axis.z);
      const across = pts.map(p => -(p.x - c.x) * axis.z + (p.z - c.z) * axis.x);
      const length = Math.max(...along) - Math.min(...along) + cell;
      const depth = Math.max(...across) - Math.min(...across) + cell;
      if (length >= BLOCK_DOOR_M[0] && length <= BLOCK_DOOR_M[1] && depth <= BLOCK_DOOR_MAX_DEPTH_M) {
        const mid = (Math.max(...along) + Math.min(...along)) / 2;
        carve({ x: c.x + axis.x * mid, z: c.z + axis.z * mid }, axis, length / 2);
      } else if (length < BLOCK_DOOR_M[0]) {
        posts.push(c);
      }
    }
    // Une porte simple d'abord (au plus près) ; une double porte si chaque montant porte un battant.
    const hinged = posts.map(p => leafEnds.some(e => Math.hypot(e.x - p.x, e.z - p.z) <= HINGE_M));
    const pairs: [number, number, number][] = [];
    for (let i = 0; i < posts.length; i++) {
      for (let k = i + 1; k < posts.length; k++) {
        const d = Math.hypot(posts[i].x - posts[k].x, posts[i].z - posts[k].z);
        const single = d >= JAMB_PAIR_M[0] && d <= JAMB_PAIR_M[1];
        const double = d > JAMB_PAIR_M[1] && d <= DOUBLE_DOOR_M && hinged[i] && hinged[k];
        if (single || double) pairs.push([d, i, k]);
      }
    }
    const paired = new Set<number>();
    for (const [d, i, k] of pairs.sort((a, b) => a[0] - b[0])) {
      if (paired.has(i) || paired.has(k)) continue;
      paired.add(i);
      paired.add(k);
      const a = posts[i];
      const b = posts[k];
      carve({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 }, { x: (b.x - a.x) / d, z: (b.z - a.z) / d }, d / 2);
    }
    for (let i = 0; i < opening.length; i++) {
      if (door[i]) opening[i] = 1;
    }

    return NavGrid.fromMasks({ x0, z0, cell, nx, nz, floor, wall, opening }, input.clearanceM ?? 0, outdoor > 0);
  }

  /**
   * Grille à partir de masques déjà tracés (cellule par cellule, `iz * nx + ix`) : plancher, murs,
   * ouvertures. Sert aux heuristiques sur la géométrie et au bloc `navigation` de l'export.
   */
  static fromMasks(
    m: {
      x0: number;
      z0: number;
      cell: number;
      nx: number;
      nz: number;
      floor: Uint8Array;
      wall: Uint8Array;
      opening: Uint8Array;
    },
    clearanceM: number,
    withOutdoor: boolean
  ): NavGrid {
    const { x0, z0, cell, nx, nz, floor, wall, opening } = m;
    // Une marge est un minimum : arrondi au-dessus (0,3 / 0,2 vaut 1,4999… en flottant).
    const r = Math.ceil(clearanceM / cell - 1e-6);
    const hard = new Uint8Array(nx * nz);
    for (let i = 0; i < hard.length; i++) {
      hard[i] = wall[i] && !opening[i] ? 1 : 0;
    }
    const outside = withOutdoor ? exterior(floor, hard, opening, nx, nz) : new Uint8Array(nx * nz);
    const wallMargin = dilate(hard, nx, nz, r);
    const openMargin = dilate(opening, nx, nz, r + 1);
    const walkable = new Uint8Array(nx * nz);
    const onFloor = new Uint8Array(nx * nz);
    for (let i = 0; i < walkable.length; i++) {
      onFloor[i] = floor[i] || opening[i] || outside[i];
      walkable[i] = onFloor[i] && !hard[i] && (!wallMargin[i] || openMargin[i]) ? 1 : 0;
    }
    return new NavGrid(x0, z0, cell, nx, nz, walkable, onFloor);
  }

  isWalkable(p: Point2): boolean {
    const c = this.cellOf(p);
    return c !== null && this.walkable[c] === 1;
  }

  /** Point marchable le plus proche à moins de `maxM` (centre de cellule), ou `null`. */
  nearestWalkable(p: Point2, maxM: number): Point2 | null {
    if (this.isWalkable(p)) {
      return p;
    }
    const ix = Math.floor((p.x - this.x0) / this.cell);
    const iz = Math.floor((p.z - this.z0) / this.cell);
    const rMax = Math.ceil(maxM / this.cell);
    let best: Point2 | null = null;
    let bestD = Infinity;
    for (let dz = -rMax; dz <= rMax; dz++) {
      for (let dx = -rMax; dx <= rMax; dx++) {
        const cx = ix + dx;
        const cz = iz + dz;
        if (cx < 0 || cz < 0 || cx >= this.nx || cz >= this.nz || !this.walkable[cz * this.nx + cx]) {
          continue;
        }
        const c = this.center(cz * this.nx + cx);
        const d = Math.hypot(c.x - p.x, c.z - p.z);
        if (d <= maxM && d < bestD) {
          bestD = d;
          best = c;
        }
      }
    }
    return best;
  }

  /**
   * Chemin de `from` à `to` (points de passage, départ exclu, arrivée incluse) contournant les
   * murs ; `null` s'il n'y en a pas. Ligne droite quand rien ne gêne.
   *
   * `allowWallCrossing` : pour une pièce sans ouverture modélisée, le mur est franchi à un seul
   * endroit, au plus court, plutôt que de ne pas trouver de chemin (une porte reste préférée).
   */
  findPath(from: Point2, to: Point2, opts: { allowWallCrossing?: boolean } = {}): Point2[] | null {
    const start = this.nearestWalkable(from, 2);
    const goal = this.nearestWalkable(to, 2);
    if (!start || !goal) {
      return null;
    }
    if (this.lineOfSight(start, goal)) {
      return [goal];
    }
    const cells = this.astar(this.cellOf(start)!, this.cellOf(goal)!, opts.allowWallCrossing ? WALL_CROSSING_COST : 0);
    if (!cells) {
      return null;
    }
    const points = cells.map(c => this.center(c));
    points[points.length - 1] = goal;
    // Lissage : du point courant, on file vers le point visible le plus loin.
    const out: Point2[] = [];
    let cur = start;
    let i = 0;
    while (i < points.length) {
      let far = i;
      for (let k = points.length - 1; k > i; k--) {
        if (this.lineOfSight(cur, points[k])) {
          far = k;
          break;
        }
      }
      cur = points[far];
      out.push(cur);
      i = far + 1;
    }
    return out;
  }

  /**
   * Point marchable, atteignable depuis `from` sans franchir de mur, le plus proche de `to` : où
   * montrer un tracker mesuré hors du bâtiment ou dans une pièce sans accès. `null` si `from` est
   * loin de toute zone marchable.
   */
  closestReachable(from: Point2, to: Point2): Point2 | null {
    const start = this.nearestWalkable(from, 2);
    if (!start) {
      return null;
    }
    const { nx, nz, walkable } = this;
    const first = this.cellOf(start)!;
    const seen = new Uint8Array(nx * nz);
    const queue = new Int32Array(nx * nz);
    let head = 0;
    let tail = 0;
    queue[tail++] = first;
    seen[first] = 1;
    let best = first;
    let bestD = Infinity;
    while (head < tail) {
      const c = queue[head++];
      const p = this.center(c);
      const d = Math.hypot(p.x - to.x, p.z - to.z);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
      const cx = c % nx;
      const cz = Math.floor(c / nx);
      for (const [dx, dz] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        const x = cx + dx;
        const z = cz + dz;
        const n = z * nx + x;
        if (x >= 0 && z >= 0 && x < nx && z < nz && walkable[n] && !seen[n]) {
          seen[n] = 1;
          queue[tail++] = n;
        }
      }
    }
    return this.center(best);
  }

  /** Vrai si le segment ne traverse que des cellules marchables. */
  lineOfSight(a: Point2, b: Point2): boolean {
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / (this.cell / 3)));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      if (!this.isWalkable({ x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t })) {
        return false;
      }
    }
    return true;
  }

  private cellOf(p: Point2): number | null {
    const ix = Math.floor((p.x - this.x0) / this.cell);
    const iz = Math.floor((p.z - this.z0) / this.cell);
    return ix >= 0 && iz >= 0 && ix < this.nx && iz < this.nz ? iz * this.nx + ix : null;
  }

  private center(c: number): Point2 {
    return { x: this.x0 + ((c % this.nx) + 0.5) * this.cell, z: this.z0 + (Math.floor(c / this.nx) + 0.5) * this.cell };
  }

  /** A* sur 8 voisins, sans couper les coins ; `wallCost` > 0 : murs du plancher franchissables à ce prix. */
  private astar(start: number, goal: number, wallCost: number): number[] | null {
    const { nx, nz, walkable, onFloor } = this;
    const passable = (c: number) => walkable[c] === 1 || (wallCost > 0 && onFloor[c] === 1);
    const g = new Float64Array(nx * nz).fill(Infinity);
    const came = new Int32Array(nx * nz).fill(-1);
    const closed = new Uint8Array(nx * nz);
    const gx = goal % nx;
    const gz = Math.floor(goal / nx);
    const h = (c: number) => {
      const dx = Math.abs((c % nx) - gx);
      const dz = Math.abs(Math.floor(c / nx) - gz);
      return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz);
    };
    const open = new MinHeap();
    g[start] = 0;
    open.push(start, h(start));
    while (open.size) {
      const c = open.pop();
      if (c === goal) {
        const path: number[] = [];
        for (let k = c; k !== start; k = came[k]) {
          path.push(k);
        }
        return path.reverse();
      }
      if (closed[c]) {
        continue;
      }
      closed[c] = 1;
      const cx = c % nx;
      const cz = Math.floor(c / nx);
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dz) {
            continue;
          }
          const x = cx + dx;
          const z = cz + dz;
          if (x < 0 || z < 0 || x >= nx || z >= nz) {
            continue;
          }
          const n = z * nx + x;
          if (!passable(n) || closed[n]) {
            continue;
          }
          if (dx && dz && (!passable(cz * nx + x) || !passable(z * nx + cx))) {
            continue;
          }
          const cost = g[c] + (dx && dz ? Math.SQRT2 : 1) * (walkable[n] ? 1 : wallCost);
          if (cost < g[n]) {
            g[n] = cost;
            came[n] = c;
            open.push(n, cost + h(n));
          }
        }
      }
    }
    return null;
  }
}

/** Tas binaire minimal (file de priorité de l'A*). */
class MinHeap {
  private readonly items: number[] = [];
  private readonly prio: number[] = [];

  get size(): number {
    return this.items.length;
  }

  push(item: number, p: number): void {
    this.items.push(item);
    this.prio.push(p);
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.prio[parent] <= this.prio[i]) {
        break;
      }
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number {
    const top = this.items[0];
    const lastItem = this.items.pop()!;
    const lastPrio = this.prio.pop()!;
    if (this.items.length) {
      this.items[0] = lastItem;
      this.prio[0] = lastPrio;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < this.items.length && this.prio[l] < this.prio[m]) m = l;
        if (r < this.items.length && this.prio[r] < this.prio[m]) m = r;
        if (m === i) break;
        this.swap(i, m);
        i = m;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    [this.items[a], this.items[b]] = [this.items[b], this.items[a]];
    [this.prio[a], this.prio[b]] = [this.prio[b], this.prio[a]];
  }
}

function horizontalNear(tris: ArrayLike<number>, y: number, tol: number): number[] {
  const out: number[] = [];
  for (let i = 0; i + 8 < tris.length; i += 9) {
    if (Math.abs(tris[i + 1] - y) <= tol && Math.abs(tris[i + 4] - y) <= tol && Math.abs(tris[i + 7] - y) <= tol) {
      for (let k = 0; k < 9; k++) out.push(tris[i + k]);
    }
  }
  return out;
}

function bbox(tris: ArrayLike<number>): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i + 2 < tris.length; i += 3) {
    minX = Math.min(minX, tris[i]);
    maxX = Math.max(maxX, tris[i]);
    minZ = Math.min(minZ, tris[i + 2]);
    maxZ = Math.max(maxZ, tris[i + 2]);
  }
  return Number.isFinite(minX) ? { minX, maxX, minZ, maxZ } : { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
}

/** Cellules dont le centre tombe dans la projection (x, z) de chaque triangle horizontal. */
function fillTriangles(
  tris: ArrayLike<number>,
  cell: number,
  mark: (x: number, z: number) => void,
  x0: number,
  z0: number,
  nx: number,
  nz: number
): void {
  for (let i = 0; i + 8 < tris.length; i += 9) {
    const ax = tris[i],
      az = tris[i + 2],
      bx = tris[i + 3],
      bz = tris[i + 5],
      cx = tris[i + 6],
      cz = tris[i + 8];
    const area = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
    if (Math.abs(area) < 1e-9) continue;
    const ix0 = Math.max(0, Math.floor((Math.min(ax, bx, cx) - x0) / cell));
    const ix1 = Math.min(nx - 1, Math.floor((Math.max(ax, bx, cx) - x0) / cell));
    const iz0 = Math.max(0, Math.floor((Math.min(az, bz, cz) - z0) / cell));
    const iz1 = Math.min(nz - 1, Math.floor((Math.max(az, bz, cz) - z0) / cell));
    for (let iz = iz0; iz <= iz1; iz++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        const px = x0 + (ix + 0.5) * cell;
        const pz = z0 + (iz + 0.5) * cell;
        const w0 = ((bx - px) * (cz - pz) - (cx - px) * (bz - pz)) / area;
        const w1 = ((cx - px) * (az - pz) - (ax - px) * (cz - pz)) / area;
        const w2 = 1 - w0 - w1;
        if (w0 >= -1e-9 && w1 >= -1e-9 && w2 >= -1e-9) mark(px, pz);
      }
    }
  }
}

/** Points de chaque triangle compris dans la tranche [yMin, yMax], échantillonnés tous les demi-cellules. */
function sampleTriangles(
  tris: ArrayLike<number>,
  cell: number,
  yMin: number,
  yMax: number,
  mark: (x: number, z: number) => void
): void {
  for (let i = 0; i + 8 < tris.length; i += 9) {
    const y0 = tris[i + 1],
      y1 = tris[i + 4],
      y2 = tris[i + 7];
    if (Math.max(y0, y1, y2) < yMin || Math.min(y0, y1, y2) > yMax) continue;
    const edge = (a: number, b: number) =>
      Math.hypot(tris[i + a] - tris[i + b], tris[i + a + 1] - tris[i + b + 1], tris[i + a + 2] - tris[i + b + 2]);
    const n = Math.max(2, Math.ceil(Math.max(edge(0, 3), edge(3, 6), edge(0, 6)) / (cell / 2)));
    for (let a = 0; a <= n; a++) {
      for (let b = 0; b <= n - a; b++) {
        const u = a / n,
          v = b / n,
          w = 1 - u - v;
        const y = u * y0 + v * y1 + w * y2;
        if (y < yMin || y > yMax) continue;
        mark(u * tris[i] + v * tris[i + 3] + w * tris[i + 6], u * tris[i + 2] + v * tris[i + 5] + w * tris[i + 8]);
      }
    }
  }
}

/**
 * Dehors : cellules hors plancher reliées au bord de la grille sans franchir de mur ni d'ouverture
 * (4 voisins, un mur en diagonale ne laisse pas passer). Un vide intérieur clos reste fermé.
 */
function exterior(floor: Uint8Array, hard: Uint8Array, opening: Uint8Array, nx: number, nz: number): Uint8Array {
  const out = new Uint8Array(nx * nz);
  const open = (c: number) => !floor[c] && !hard[c] && !opening[c];
  const stack: number[] = [];
  const seed = (c: number) => {
    if (!out[c] && open(c)) {
      out[c] = 1;
      stack.push(c);
    }
  };
  for (let x = 0; x < nx; x++) {
    seed(x);
    seed((nz - 1) * nx + x);
  }
  for (let z = 0; z < nz; z++) {
    seed(z * nx);
    seed(z * nx + nx - 1);
  }
  while (stack.length) {
    const c = stack.pop()!;
    const cx = c % nx;
    const cz = Math.floor(c / nx);
    if (cx > 0) seed(c - 1);
    if (cx < nx - 1) seed(c + 1);
    if (cz > 0) seed(c - nx);
    if (cz < nz - 1) seed(c + nx);
  }
  return out;
}

/** Dilatation carrée de rayon `r` cellules. */
function dilate(m: Uint8Array, nx: number, nz: number, r: number): Uint8Array {
  if (r <= 0) return m;
  const out = new Uint8Array(m.length);
  for (let z = 0; z < nz; z++) {
    for (let x = 0; x < nx; x++) {
      if (!m[z * nx + x]) continue;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const a = x + dx,
            b = z + dz;
          if (a >= 0 && b >= 0 && a < nx && b < nz) out[b * nx + a] = 1;
        }
      }
    }
  }
  return out;
}

/** Groupes de cellules marquées contiguës (8 voisins). */
function components(m: Uint8Array, nx: number, nz: number): number[][] {
  const seen = new Uint8Array(m.length);
  const out: number[][] = [];
  for (let i = 0; i < m.length; i++) {
    if (!m[i] || seen[i]) continue;
    const group: number[] = [];
    const stack = [i];
    seen[i] = 1;
    while (stack.length) {
      const c = stack.pop()!;
      group.push(c);
      const cx = c % nx;
      const cz = Math.floor(c / nx);
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          const x = cx + dx;
          const z = cz + dz;
          const n = z * nx + x;
          if (x >= 0 && z >= 0 && x < nx && z < nz && m[n] && !seen[n]) {
            seen[n] = 1;
            stack.push(n);
          }
        }
      }
    }
    out.push(group);
  }
  return out;
}

function centroid(pts: { x: number; z: number }[]): { x: number; z: number } {
  return { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, z: pts.reduce((a, p) => a + p.z, 0) / pts.length };
}

/** Direction principale d'un nuage de points (le long d'un battant de porte). */
function principalAxis(pts: { x: number; z: number }[], c: { x: number; z: number }): { x: number; z: number } {
  let sxx = 0;
  let szz = 0;
  let sxz = 0;
  for (const p of pts) {
    sxx += (p.x - c.x) ** 2;
    szz += (p.z - c.z) ** 2;
    sxz += (p.x - c.x) * (p.z - c.z);
  }
  const th = 0.5 * Math.atan2(2 * sxz, sxx - szz);
  return { x: Math.cos(th), z: Math.sin(th) };
}
